import { useEffect } from 'react';
import clsx from 'clsx';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateUnderline } from '../../api/queries';
import styles from './UnderlineView.module.css';

type Importance = 'VERY_IMPORTANT' | 'IMPORTANT' | 'SECONDARY' | string;
type Concept = {
  text?: string;
  importance?: Importance;
  explanation?: string;
};

const IMPORTANCE_LABEL: Record<string, string> = {
  VERY_IMPORTANT: 'Muy importante',
  IMPORTANT: 'Importante',
  SECONDARY: 'Secundario'
};

const importanceClass = (imp?: string) => {
  switch (imp) {
    case 'VERY_IMPORTANT':
      return styles.impHigh;
    case 'IMPORTANT':
      return styles.impMid;
    case 'SECONDARY':
      return styles.impLow;
    default:
      return styles.impMid;
  }
};

export function UnderlineView() {
  const mut = useGenerateUnderline();
  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending)
    return <LoadingState label="Subrayando lo importante…" hint="Marcando las ideas que merece la pena retener" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;
  const c = data.content ?? {};

  const html: string = c.html ?? '';
  const summary: string = c.summary ?? '';
  const concepts: Concept[] = Array.isArray(c.concepts) ? c.concepts : [];

  return (
    <div className={styles.view}>
      {summary && (
        <section className={styles.summaryBox}>
          <span className={styles.summaryLabel}>En pocas palabras</span>
          <p>{summary}</p>
        </section>
      )}

      {html && (
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Texto subrayado</h3>
          {/* Backend-controlled HTML: only <strong> wraps the highlighted
              terms, and CSS turns those into yellow highlights. */}
          <div
            className={styles.passage}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </section>
      )}

      {concepts.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Conceptos clave</h3>
          <ul className={styles.concepts}>
            {concepts.map((k, i) => (
              <li key={i} className={styles.concept}>
                <div className={styles.conceptHead}>
                  <span className={styles.conceptText}>{k.text}</span>
                  {k.importance && (
                    <span className={clsx(styles.imp, importanceClass(k.importance))}>
                      {IMPORTANCE_LABEL[k.importance] ?? k.importance}
                    </span>
                  )}
                </div>
                {k.explanation && (
                  <p className={styles.conceptBody}>{k.explanation}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
